import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, EyeOff, FilePlus2, Plus, SlidersHorizontal, Trash2, Upload, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { useEnlacesEnLaWeb } from '../api/enlaces'
import { COLECCIONES_POR_IDIOMA, idiomaDeLaDireccion, NOMBRE_DE_IDIOMA, useIdiomas } from '../api/idiomas'
import { consulta, type ErrorDeLaApi, pedir } from '../api/nucleo'
import { type AccionDeLote, aplicarEnLote } from '../api/publicacion'
import type { Lista } from '../api/tipos'
import { Aviso, BarraDeBusqueda, Boton, Casilla, Esqueleto, EstadoVacio, Modal, Pastilla, Selector, Tarjeta, Titulo, clases } from '../componentes/ui'
import { aplanar, useEsquema, type Campo } from './esquema'
import { esFemenino, nuevoDe } from './genero'
import { accionesDisponibles, alternarTodos, alternarUno, confirmacionDelLote, estadoDeTodos, etiquetaDeAccion, resumenDelLote, soloVisibles } from './seleccion'
import { CeldaDeDireccion } from './VerEnLaWeb'

/**
 * El listado genérico de una colección: columnas del esquema (`admin.defaultColumns`),
 * búsqueda por el título —que busca sola al escribir—, filtros por los campos de
 * opciones, orden por columna y paginación. Todo va en la dirección (`?q=&pagina=&
 * orden=&f_estado=`), así que un listado filtrado se puede compartir y el botón «atrás»
 * vuelve a donde estaba.
 *
 * Con casillas, si el esquema dice que quien mira puede hacer algo en lote (publicar, pasar
 * a borrador, borrar): se marca fila a fila o todo lo visible, y cada acción se confirma
 * antes y se resume después. Lo que se hace lo decide el núcleo en cada id.
 *
 * Siempre filtra por sitio (o por cliente). El acceso lo acota el núcleo; esto es para
 * no traerse la web de al lado y esconderla con CSS.
 */
type Doc = Record<string, unknown> & { id: number }

export type Filtro = { clave: string; etiqueta: string; opciones: Array<{ etiqueta: string; valor: string }> }

export type ColumnaPropia = { clave: string; etiqueta: string; celda: (doc: Doc) => ReactNode; ordenable?: boolean }

/** La columna «Dirección» de lo que se ve en la web: la pone el listado solo, si el esquema dice `enLaWeb`. */
const COLUMNA_DIRECCION = '__direccion'

type Props = {
  coleccion: string
  sitio: null | number
  espacio: number
  base: string
  titulo?: string
  descripcion?: ReactNode
  /** Sustituye a las columnas del esquema. */
  columnas?: string[]
  columnasPropias?: Record<string, ColumnaPropia>
  filtrosExtra?: Filtro[]
  /** Un `where` fijo, además del sitio: `{ 'where[estado][not_equals]': 'spam' }`. */
  where?: Record<string, string>
  /** El filtro de pertenencia cuando no es `cliente` ni `sitio` (usuarios: `clientes.cliente`). */
  whereBase?: Record<string, string>
  puedeCrear?: boolean
  sinSitio?: boolean
  porCliente?: boolean
  accion?: ReactNode
  /** Algo que decir encima de la lista (p. ej. en Productos, la última importación). */
  aviso?: ReactNode
  /** Qué abre una fila; por omisión, su editor. */
  enlaceDe?: (doc: Doc) => string
  vacio?: string
  porPagina?: number
  /**
   * La búsqueda, los filtros, el orden y la página en un estado propio en vez de en la
   * dirección: para un listado dentro de otra pantalla (las respuestas de un formulario,
   * en su constructor), donde cambiar la dirección movería esa pantalla.
   */
  parametros?: { valor: URLSearchParams; poner: (siguientes: URLSearchParams) => void }
  /** Sin el título grande de página: el listado es una parte de otra pantalla. */
  compacto?: boolean
  /** Cómo se llama lo que se lista, si no es lo que dice el esquema (los contenidos de un tipo son «Propiedades»). */
  etiquetas?: { singular: string; plural: string }
}

const fecha = (v: unknown) => (typeof v === 'string' && v ? new Date(v).toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')

const tituloDeRelacion = (v: unknown): string => {
  if (v === null || v === undefined) return '—'
  if (Array.isArray(v)) return v.map(tituloDeRelacion).join(', ')
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    if ('value' in o && typeof o.value === 'object') return tituloDeRelacion(o.value)
    for (const k of ['titulo', 'nombre', 'alt', 'email', 'ubicacion', 'comentario', 'ruta', 'origen', 'numero', 'slug']) {
      if (typeof o[k] === 'string' && o[k]) return String(o[k])
      if (typeof o[k] === 'number') return String(o[k])
    }
    return `#${String(o.id ?? '')}`
  }
  return `#${String(v)}`
}

/** Una relación que llegó sin poblar (`maxDepth: 0`, o borradores): se pide su título aparte, con caché. */
const EtiquetaDeRelacion = ({ coleccion, id }: { coleccion: string; id: number }) => {
  const { data: esquema } = useEsquema(coleccion)
  const { data } = useQuery({
    queryKey: ['relacion', coleccion, String(id)],
    queryFn: async () => (await pedir<Lista<Doc>>(`/${coleccion}${consulta({ 'where[id][in]': String(id), limit: 1, depth: 0 })}`)).docs,
  })
  const doc = data?.[0]
  const v = doc && esquema ? doc[esquema.titulo] : undefined
  return <>{typeof v === 'string' && v ? v : typeof v === 'number' ? String(v) : doc ? `#${id}` : '…'}</>
}

/**
 * El estado de un documento con borradores. El listado lee la última versión, así que lo
 * publicado con cambios guardados encima llega como borrador: se dice lo que es, porque en
 * internet sigue lo de antes y hay algo por publicar.
 */
export const pastillaDeEstado = (valor: unknown, conCambios = false): ReactNode =>
  valor === 'published' ? (
    <Pastilla clave="publicado">Publicado</Pastilla>
  ) : conCambios ? (
    <Pastilla clave="construyendo">Publicado · con cambios sin publicar</Pastilla>
  ) : (
    <Pastilla clave="sin-publicar">Borrador</Pastilla>
  )

export const celdaGenerica = (campo: Campo | undefined, valor: unknown, clave: string): ReactNode => {
  if (clave === '_status') return pastillaDeEstado(valor)
  if (clave === 'createdAt' || clave === 'updatedAt' || campo?.tipo === 'date') return <span className="text-tenue tabular-nums">{fecha(valor)}</span>
  if (!campo) return typeof valor === 'object' ? tituloDeRelacion(valor) : String(valor ?? '—')
  switch (campo.tipo) {
    case 'checkbox':
      return valor ? <Pastilla clave="publicado" punto={false}>Sí</Pastilla> : <span className="text-tenue">No</span>
    case 'select':
    case 'radio': {
      const lista = Array.isArray(valor) ? valor : [valor]
      const texto = lista.map((v) => campo.opciones?.find((o) => o.valor === v)?.etiqueta ?? String(v ?? '—')).join(', ')
      return campo.nombre === 'estado' ? <Pastilla clave="neutra">{texto}</Pastilla> : texto
    }
    case 'relationship':
    case 'upload': {
      const destino = Array.isArray(campo.relacion) ? null : campo.relacion
      if (typeof valor === 'number' && destino) return <EtiquetaDeRelacion coleccion={destino} id={valor} />
      if (Array.isArray(valor) && destino && valor.every((v) => typeof v === 'number')) {
        return (
          <>
            {valor.map((v, i) => (
              <span key={String(v)}>
                {i > 0 && ', '}
                <EtiquetaDeRelacion coleccion={destino} id={v as number} />
              </span>
            ))}
          </>
        )
      }
      return tituloDeRelacion(valor)
    }
    case 'number':
      return <span className="tabular-nums">{typeof valor === 'number' ? valor.toLocaleString('es') : '—'}</span>
    case 'richText':
    case 'json':
    case 'array':
    case 'blocks':
    case 'group':
      return <span className="text-tenue">…</span>
    default:
      return typeof valor === 'string' ? valor : valor === null || valor === undefined ? '—' : String(valor)
  }
}

export const Listado = (p: Props) => {
  const navegar = useNavigate()
  const [paramsDeLaDireccion, setParamsDeLaDireccion] = useSearchParams()
  const params = p.parametros?.valor ?? paramsDeLaDireccion
  const setParams = (siguientes: URLSearchParams, opciones?: { replace?: boolean }) => (p.parametros ? p.parametros.poner(siguientes) : setParamsDeLaDireccion(siguientes, opciones))
  const { data: esquema, isPending: cargandoEsquema } = useEsquema(p.coleccion)
  const q = params.get('q') ?? ''
  const pagina = Math.max(1, Number(params.get('pagina')) || 1)
  // Lo que va por idioma se lista de uno en uno (§37): el español por omisión, y pestañas si la web tiene más.
  const porIdioma = COLECCIONES_POR_IDIOMA.has(p.coleccion) && !p.sinSitio && !p.porCliente && p.sitio !== null
  const { data: idiomasDeLaWeb } = useIdiomas(porIdioma ? p.sitio : null)
  const idioma = idiomaDeLaDireccion(params.get('idioma'), idiomasDeLaWeb?.encendidos)
  const variosIdiomas = porIdioma && (idiomasDeLaWeb?.encendidos.length ?? 1) > 1
  // «Nuevo» en la pestaña del inglés crea una versión que solo existe en inglés.
  const enlaceDeNuevo = `${p.base}/nuevo${idioma !== 'es' ? `?idioma=${idioma}` : ''}`
  const orden = params.get('orden') ?? ''

  const poner = (cambios: Record<string, null | string>) => {
    const siguientes = new URLSearchParams(params)
    for (const [k, v] of Object.entries(cambios)) v === null || v === '' ? siguientes.delete(k) : siguientes.set(k, v)
    if (!('pagina' in cambios)) siguientes.delete('pagina')
    setParams(siguientes, { replace: true })
  }

  const campos = esquema ? aplanar(esquema.campos) : []
  // Lo que tiene dirección en la web lleva su columna (la dirección corta, copiar y abrir; o la vista previa de un borrador).
  const conDireccion = Boolean(esquema?.enLaWeb) && !p.sinSitio && !p.porCliente && p.sitio !== null
  const campoDe = (nombre: string) => campos.find((c) => c.nombre === nombre)
  // Con la dirección completa, el último tramo («miraflores») sobra: serían dos columnas «Dirección».
  const columnas = [
    ...(p.columnas ?? esquema?.columnas ?? []).filter((c) => c !== 'cliente' && c !== 'sitio' && !(conDireccion && c === 'slug')),
    ...(conDireccion ? [COLUMNA_DIRECCION] : []),
  ]

  // Filtros: los propios del módulo, el estado si hay borradores, y todo campo de opciones que sea columna.
  const filtros: Filtro[] = [
    ...(p.filtrosExtra ?? []),
    // «Estado» es el de publicación, salvo que la colección tenga su propio «Estado» (un tipo de contenido: activo o archivado).
    ...(esquema?.borradores ? [{ clave: '_status', etiqueta: columnas.some((c) => c !== '_status' && campoDe(c)?.etiqueta === 'Estado') ? 'Publicación' : 'Estado', opciones: [{ etiqueta: 'Publicado', valor: 'published' }, { etiqueta: 'Borrador', valor: 'draft' }] }] : []),
    ...columnas
      .map((c) => campoDe(c))
      .filter(
        (c): c is Campo =>
          Boolean(c && (c.tipo === 'select' || c.tipo === 'radio' || c.tipo === 'checkbox') && c.nombre && c.nombre !== '_status' && !(p.filtrosExtra ?? []).some((f) => f.clave === c.nombre)),
      )
      .map((c) => ({
        clave: c.nombre!,
        etiqueta: c.etiqueta,
        opciones: c.tipo === 'checkbox' ? [{ etiqueta: 'Sí', valor: 'true' }, { etiqueta: 'No', valor: 'false' }] : (c.opciones ?? []),
      })),
  ]
  const activos = filtros.map((f) => [f.clave, params.get(`f_${f.clave}`)] as const).filter(([, v]) => v)

  const where: Record<string, string | number> = {
    ...(p.sinSitio ? {} : p.porCliente || !p.sitio ? { 'where[cliente][equals]': p.espacio } : { 'where[sitio][equals]': p.sitio }),
    ...(p.whereBase ?? {}),
    ...(p.where ?? {}),
    ...(q && esquema ? { [`where[${esquema.titulo}][contains]`]: q } : {}),
    ...(porIdioma ? { 'where[idioma][equals]': idioma } : {}),
    ...Object.fromEntries(activos.map(([k, v]) => [`where[${k}][equals]`, v as string])),
  }

  const { data, isPending, error } = useQuery({
    queryKey: ['lista', p.coleccion, where, pagina, orden, p.porPagina],
    enabled: Boolean(esquema),
    queryFn: () =>
      pedir<Lista<Doc>>(
        `/${p.coleccion}${consulta({ ...where, limit: p.porPagina ?? 25, page: pagina, sort: orden || `-${esquema?.borradores || campoDe('updatedAt') ? 'updatedAt' : 'createdAt'}`, depth: 1, ...(esquema?.borradores ? { draft: true } : {}) })}`,
      ),
  })

  // De lo que llega como borrador, lo que en internet está publicado: tiene cambios encima sin publicar.
  // Se pregunta a la tabla principal (sin `draft`) por esos ids. La clave cuelga de «lista»: se olvida con ella.
  const idsEnBorrador = esquema?.borradores ? (data?.docs ?? []).filter((d) => d._status === 'draft').map((d) => d.id) : []
  const { data: conCambios } = useQuery({
    queryKey: ['lista', p.coleccion, 'con-cambios', idsEnBorrador.join(',')],
    enabled: idsEnBorrador.length > 0,
    queryFn: async () =>
      new Set(
        (await pedir<Lista<{ id: number }>>(`/${p.coleccion}${consulta({ 'where[id][in]': idsEnBorrador.join(','), 'where[_status][equals]': 'published', limit: idsEnBorrador.length, depth: 0, 'select[id]': 'true' })}`)).docs.map((d) => d.id),
      ),
  })

  // Las casillas: solo si el esquema ofrece alguna acción en lote a quien mira, y sobre una web.
  const clienteDeConsultas = useQueryClient()
  const acciones = accionesDisponibles(esquema?.acciones)
  const conCasillas = acciones.length > 0 && p.sitio !== null && !p.sinSitio && !p.porCliente
  const [seleccion, setSeleccion] = useState<Set<number>>(() => new Set())
  const [confirmar, setConfirmar] = useState<AccionDeLote | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [resultadoDelLote, setResultadoDelLote] = useState<null | { texto: string; tono: 'aviso' | 'error' | 'ok' }>(null)
  const visibles = (data?.docs ?? []).map((d) => d.id)
  const claveDeVisibles = visibles.join(',')
  // «Ver en la web ↗» junto al título de lo que se ve en la web: una sola petición por página del listado.
  const { data: enlaces } = useEnlacesEnLaWeb(p.coleccion, p.sitio, visibles, conDireccion)
  const propias: Record<string, ColumnaPropia> = {
    ...(p.columnasPropias ?? {}),
    ...(conDireccion
      ? {
          [COLUMNA_DIRECCION]: {
            clave: COLUMNA_DIRECCION,
            etiqueta: 'Dirección',
            ordenable: false,
            celda: (doc: Doc) => (
              <CeldaDeDireccion borrador={doc._status === 'draft' && !conCambios?.has(doc.id)} coleccion={p.coleccion} enlace={enlaces?.enlaces[String(doc.id)]} id={doc.id} sitio={p.sitio} />
            ),
          },
        }
      : {}),
  }
  // Al cambiar de página, de filtro o al refrescar, lo que ya no se ve deja de estar marcado.
  useEffect(() => {
    setSeleccion((s) => soloVisibles(s, claveDeVisibles ? claveDeVisibles.split(',').map(Number) : []))
  }, [claveDeVisibles])

  const aplicar = async (accion: AccionDeLote) => {
    if (!p.sitio) return
    setOcupado(true)
    try {
      const r = await aplicarEnLote(p.coleccion, p.sitio, [...seleccion], accion)
      setResultadoDelLote(resumenDelLote(accion, r))
      setSeleccion(new Set())
    } catch (e) {
      setResultadoDelLote({ texto: (e as ErrorDeLaApi).message || 'No se ha podido hacer. Vuelve a intentarlo.', tono: 'error' })
    } finally {
      setOcupado(false)
      setConfirmar(null)
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', p.coleccion] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['web-pendiente', p.sitio] })
      void clienteDeConsultas.invalidateQueries({ queryKey: ['diseno-estado', p.sitio] })
      if (accion === 'borrar') void clienteDeConsultas.invalidateQueries({ queryKey: ['relacion', p.coleccion] })
    }
  }

  if (cargandoEsquema) return <Esqueleto />
  if (!esquema) return <Aviso tono="error">No se ha podido leer la definición de esta sección.</Aviso>

  const singular = (p.etiquetas ?? esquema.etiqueta).singular.toLowerCase()
  const plural = (p.etiquetas ?? esquema.etiqueta).plural.toLowerCase()
  const ordenarPor = (clave: string) => poner({ orden: orden === clave ? `-${clave}` : orden === `-${clave}` ? '' : clave })
  const flechaDe = (clave: string) => (orden === clave ? <ArrowUp className="size-3.5" /> : orden === `-${clave}` ? <ArrowDown className="size-3.5" /> : null)
  const hayFiltros = Boolean(q || activos.length > 0 || orden)
  const totalPaginas = data?.totalPages ?? 1

  const botones = (
    <>
      {p.accion}
      {p.puedeCrear !== false && (
        <Link to={enlaceDeNuevo}>
          <Boton icono={<Plus />} tipo="primario">
            {nuevoDe(singular)}
          </Boton>
        </Link>
      )}
    </>
  )

  return (
    <>
      {p.compacto ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {p.descripcion ? <p className="text-sm text-tenue">{p.descripcion}</p> : <span />}
          <div className="flex flex-wrap items-center gap-2">{botones}</div>
        </div>
      ) : (
        <Titulo accion={botones} descripcion={p.descripcion}>
          {p.titulo ?? (p.etiquetas ?? esquema.etiqueta).plural}
        </Titulo>
      )}

      {p.aviso}

      {resultadoDelLote && (
        <div className="mb-4 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Aviso tono={resultadoDelLote.tono}>{resultadoDelLote.texto}</Aviso>
          </div>
          <Boton aria-label="Cerrar el aviso" onClick={() => setResultadoDelLote(null)} pequeno tipo="discreto">
            <X aria-hidden className="size-4" />
          </Boton>
        </div>
      )}

      {confirmar && (
        <Modal
          ancho="max-w-md"
          cerrar={() => !ocupado && setConfirmar(null)}
          pie={
            <>
              <Boton disabled={ocupado} onClick={() => setConfirmar(null)}>
                Cancelar
              </Boton>
              <Boton cargando={ocupado} data-autofocus onClick={() => void aplicar(confirmar)} tipo={confirmar === 'borrar' ? 'peligro' : 'primario'}>
                {confirmacionDelLote(confirmar, seleccion.size, p.etiquetas ?? esquema.etiqueta).boton}
              </Boton>
            </>
          }
          titulo={confirmacionDelLote(confirmar, seleccion.size, p.etiquetas ?? esquema.etiqueta).titulo}
        >
          <p className="text-sm">{confirmacionDelLote(confirmar, seleccion.size, p.etiquetas ?? esquema.etiqueta).texto}</p>
        </Modal>
      )}

      {variosIdiomas && idiomasDeLaWeb && (
        // Una pestaña por idioma de la web: cada uno tiene sus propias páginas (§37).
        <div aria-label="Idioma" className="mb-3 flex flex-wrap gap-1" role="tablist">
          {idiomasDeLaWeb.encendidos.map((codigo) => (
            <button
              aria-selected={codigo === idioma}
              className={clases('rounded-pastilla border px-3 py-1 text-sm', codigo === idioma ? 'border-violeta bg-violeta-suave font-medium text-violeta' : 'border-linea text-tenue hover:text-tinta')}
              key={codigo}
              onClick={() => poner({ idioma: codigo === 'es' ? null : codigo })}
              role="tab"
              type="button"
            >
              {NOMBRE_DE_IDIOMA[codigo]}
            </button>
          ))}
        </div>
      )}
      <Tarjeta sinRelleno>
        {/* La barra de herramientas: búsqueda y filtros, compactos */}
        <div className="flex flex-wrap items-center gap-2 border-b border-linea px-4 py-3">
          <BarraDeBusqueda alBuscar={(t) => poner({ q: t })} className="w-full sm:w-72" placeholder={`Buscar en ${plural}…`} valor={q} />
          {filtros.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <SlidersHorizontal aria-hidden className="hidden size-4 text-tenue sm:block" />
              {filtros.map((f) => (
                <label className="inline-flex items-center gap-1.5 text-[13px]" key={f.clave}>
                  <span className="sr-only">{f.etiqueta}</span>
                  <Selector className="w-auto min-w-36" onChange={(e) => poner({ [`f_${f.clave}`]: e.target.value })} pequeno value={params.get(`f_${f.clave}`) ?? ''}>
                    <option value="">{f.etiqueta}: todos</option>
                    {f.opciones.map((o) => (
                      <option key={o.valor} value={o.valor}>
                        {o.etiqueta}
                      </option>
                    ))}
                  </Selector>
                </label>
              ))}
            </div>
          )}
          {hayFiltros && (
            <Boton className="ml-auto" onClick={() => setParams(new URLSearchParams(), { replace: true })} pequeno tipo="discreto">
              Limpiar
            </Boton>
          )}
        </div>

        {/* Lo que se puede hacer con lo marcado: aparece en cuanto hay algo marcado. */}
        {conCasillas && seleccion.size > 0 && (
          <div aria-label="Acciones con lo marcado" className="flex flex-wrap items-center gap-2 border-b border-linea bg-violeta-suave/40 px-4 py-2.5" role="toolbar">
            <span className="text-[13px] font-medium">
              {seleccion.size} {seleccion.size === 1 ? 'marcado' : 'marcados'}
            </span>
            {acciones.map((accion) => (
              <Boton
                disabled={ocupado}
                icono={accion === 'publicar' ? <Upload /> : accion === 'despublicar' ? <EyeOff /> : <Trash2 />}
                key={accion}
                onClick={() => setConfirmar(accion)}
                pequeno
                tipo={accion === 'borrar' ? 'peligro' : accion === 'publicar' ? 'primario' : 'secundario'}
              >
                {etiquetaDeAccion(accion)}
              </Boton>
            ))}
            <Boton className="ml-auto" onClick={() => setSeleccion(new Set())} pequeno tipo="discreto">
              Quitar la selección
            </Boton>
          </div>
        )}

        {error ? (
          <div className="p-5">
            <Aviso tono="error">No se ha podido cargar el listado. Vuelve a intentarlo.</Aviso>
          </div>
        ) : isPending ? (
          <Esqueleto />
        ) : !data?.docs.length ? (
          <div className="p-5">
            {hayFiltros ? (
              <EstadoVacio
                accion={
                  <Boton onClick={() => setParams(new URLSearchParams(), { replace: true })} pequeno>
                    Quitar los filtros
                  </Boton>
                }
                descripcion="Prueba con otras palabras o quita algún filtro."
                titulo="Nada coincide con lo que buscas"
              />
            ) : (
              <EstadoVacio
                accion={
                  p.puedeCrear !== false ? (
                    <Link to={enlaceDeNuevo}>
                      <Boton icono={<Plus />} tipo="primario">
                        Crear {esFemenino(singular) ? 'la primera' : 'el primero'}
                      </Boton>
                    </Link>
                  ) : undefined
                }
                descripcion={p.vacio ?? (p.puedeCrear !== false ? `Crea ${esFemenino(singular) ? 'la primera' : 'el primero'} y aparecerá aquí, con su estado y su fecha.` : 'Cuando haya algo, aparecerá aquí.')}
                icono={<FilePlus2 />}
                titulo={`Todavía no tienes ${plural}`}
              />
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-linea bg-papel-2 text-left text-xs text-tenue">
                    {conCasillas && (
                      <th className="w-10 py-2.5 pl-4">
                        <Casilla
                          aMedias={estadoDeTodos(seleccion, visibles) === 'algunos'}
                          etiqueta="Seleccionar todo lo visible"
                          marcada={estadoDeTodos(seleccion, visibles) === 'todos'}
                          onChange={() => setSeleccion(alternarTodos(seleccion, visibles))}
                        />
                      </th>
                    )}
                    {columnas.map((c) => {
                      const propia = propias[c]
                      const campo = campoDe(c)
                      const ordenable = propia
                        ? propia.ordenable !== false
                        : Boolean(campo && !['relationship', 'upload', 'array', 'blocks', 'group', 'json', 'richText'].includes(campo.tipo)) || c === 'updatedAt' || c === 'createdAt' || c === '_status'
                      const etiqueta = propia?.etiqueta ?? (c === '_status' ? 'Estado' : c === 'updatedAt' ? 'Modificado' : c === 'createdAt' ? 'Creado' : (campo?.etiqueta ?? c))
                      return (
                        <th className="px-4 py-2.5 font-medium whitespace-nowrap" key={c}>
                          {ordenable ? (
                            <button className={clases('inline-flex items-center gap-1 hover:text-tinta', (orden === c || orden === `-${c}`) && 'text-tinta')} onClick={() => ordenarPor(c)} type="button">
                              {etiqueta}
                              {flechaDe(c)}
                            </button>
                          ) : (
                            etiqueta
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.docs.map((doc) => {
                    const destino = p.enlaceDe ? p.enlaceDe(doc) : `${p.base}/${doc.id}`
                    const marcada = seleccion.has(doc.id)
                    return (
                      <tr
                        className={clases('group cursor-pointer border-b border-linea last:border-b-0 hover:bg-papel-2', marcada && 'bg-violeta-suave/30')}
                        key={doc.id}
                        onClick={(e) => {
                          // La casilla marca; el resto de la fila abre el documento.
                          if ((e.target as HTMLElement).closest('a,button,input,label,[data-casilla]')) return
                          navegar(destino)
                        }}
                      >
                        {conCasillas && (
                          <td className="w-10 py-3 pl-4" data-casilla>
                            <Casilla etiqueta={`Marcar «${String(doc[esquema.titulo] ?? `#${doc.id}`)}»`} marcada={marcada} onChange={() => setSeleccion(alternarUno(seleccion, doc.id))} />
                          </td>
                        )}
                        {columnas.map((c, i) => {
                          const propia = propias[c]
                          const contenido = propia ? propia.celda(doc) : c === '_status' ? pastillaDeEstado(doc[c], conCambios?.has(doc.id)) : celdaGenerica(campoDe(c), doc[c], c)
                          return (
                            <td className={clases('px-4 py-3', i === 0 ? 'font-medium text-tinta' : 'text-tinta-2')} key={c}>
                              {i === 0 ? (
                                <>
                                  <Link className="group-hover:text-violeta" to={destino}>
                                    {contenido || <span className="text-tenue">Sin título</span>}
                                  </Link>
                                </>
                              ) : (
                                contenido
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-linea px-4 py-2.5 text-[13px] text-tenue">
              <span>
                {data.totalDocs.toLocaleString('es')} {data.totalDocs === 1 ? singular : plural}
                {totalPaginas > 1 ? ` · página ${pagina} de ${totalPaginas}` : ''}
              </span>
              {totalPaginas > 1 && (
                <div className="flex items-center gap-1">
                  <Boton disabled={pagina <= 1} icono={<ChevronLeft />} onClick={() => poner({ pagina: String(pagina - 1) })} pequeno>
                    Anterior
                  </Boton>
                  <Boton disabled={pagina >= totalPaginas} onClick={() => poner({ pagina: String(pagina + 1) })} pequeno>
                    Siguiente
                    <ChevronRight className="size-4" />
                  </Boton>
                </div>
              )}
            </div>
          </>
        )}
      </Tarjeta>
    </>
  )
}
